import { supabase } from "../supabase";
import { pmError } from "./errors";
import { safeNum, emailFilterValue, escapeFilterValue , pgrestQuote, ACTIVE_LEASE} from "./helpers";
import { COMPANY_DEFAULTS } from "../config";

// ============ COMPANY-SCOPED SUPABASE HELPERS ============
// Use these instead of raw supabase.from() to automatically filter by company_id
export function companyQuery(table, companyId) {
  if (!companyId) throw new Error("companyQuery: companyId is required");
  return supabase.from(table).select("*").eq("company_id", companyId);
}

// In-memory cache of the current session's active memberships, keyed by
// user email. Populated lazily on the first guarded write so read-path
// calls aren't penalized. Cleared on sign-out via clearMembershipCache().
// This is defense-in-depth layered on top of RLS: if a table's RLS
// policy ever slips (e.g. a USING (true) grant added to a newly-created
// table during a migration), this guard still stops a writer from
// inserting into a company they aren't a member of.
let _membershipCache = null; // { email, active: Set<companyId>, fetchedAt }
const MEMBERSHIP_TTL_MS = 5 * 60 * 1000;

export function clearMembershipCache() { _membershipCache = null; }

async function ensureMembership(companyId) {
  const { data: { user } } = await supabase.auth.getUser();
  const email = (user?.email || "").toLowerCase();
  if (!email) throw new Error("companyWrite: no authenticated user");
  const stale = !_membershipCache
    || _membershipCache.email !== email
    || (Date.now() - _membershipCache.fetchedAt > MEMBERSHIP_TTL_MS);
  if (stale) {
    const { data: mems, error: memErr } = await supabase.from("company_members")
      .select("company_id, status").ilike("user_email", emailFilterValue(email));
    if (memErr) {
      // Don't block writes on a flaky membership fetch — fall through
      // to RLS as the only gate and log the miss.
      pmError("PM-1006", { raw: memErr, context: "membership cache fetch — falling back to RLS", silent: true });
      return;
    }
    _membershipCache = {
      email,
      active: new Set((mems || []).filter(m => m.status === "active").map(m => m.company_id)),
      fetchedAt: Date.now(),
    };
  }
  if (!_membershipCache.active.has(companyId)) {
    throw new Error(`companyWrite: caller ${email} is not an active member of company ${companyId}`);
  }
}

export async function companyInsert(table, rows, companyId) {
  if (!companyId) throw new Error("companyInsert: companyId is required");
  await ensureMembership(companyId);
  const cid = companyId;
  const withCompany = (Array.isArray(rows) ? rows : [rows]).map(r => ({ ...r, company_id: cid }));
  return supabase.from(table).insert(withCompany);
}
export async function companyUpsert(table, rows, companyId, onConflict) {
  if (!companyId) throw new Error("companyUpsert: companyId is required");
  await ensureMembership(companyId);
  const cid = companyId;
  const withCompany = (Array.isArray(rows) ? rows : [rows]).map(r => ({ ...r, company_id: cid }));
  return supabase.from(table).upsert(withCompany, onConflict ? { onConflict } : undefined);
}

// RPC Health Check — validates critical database dependencies on app load.
//
// Previously fired one no-op call per RPC on every page load. Each call
// hit Postgres with {} args, generated a permission/auth error, and
// appeared in DB logs — noisy at best, potentially side-effectful for
// RPCs that do partial work before arg validation. Now:
//   - Cached in localStorage for 24h, keyed by companyId. Most page
//     loads skip the probe entirely.
//   - Only fires once per process lifetime regardless of cache.
const _rpcHealthChecked = new Set();
const RPC_HEALTH_TTL_MS = 24 * 60 * 60 * 1000;

export async function checkRPCHealth(_companyId) {
  // Health check disabled 2026-04-25.
  //
  // The original implementation called each required RPC with `{}` to
  // test existence, which fired 4 spurious HTTP errors per session
  // (PGRST202 because the RPCs require parameters). Even after switching
  // to sentinel args, probing functions like update_tenant_balance with
  // p_tenant_id=-1 still returns a 400 "Tenant not found" — same console-
  // and Sentry-noise problem.
  //
  // The actual call sites for these RPCs already have fallbacks
  // (Properties.js:2577 falls back to a direct UPDATE on archive_property
  // failure; CompanySelector.js:140 falls back to client-side inserts on
  // create_company_atomic failure). So a missing/broken RPC surfaces in
  // the live error_log via pmError when an admin tries the operation,
  // not via a startup probe.
  //
  // Returning [] here means callers see "no missing RPCs" — which is
  // accurate as long as the GRANT EXECUTE migration (20260425000001) is
  // applied. If we ever need active monitoring again, build a single
  // pg_proc-querying RPC instead of N per-function probes.
  return [];
}

// ============ DATA INTEGRITY GUARDS (PM-9xxx) ============
// Hard caps on every scan. These are deliberately generous for real portfolios
// but bound the cost of an insider / runaway call — an admin clicking "Run
// Health Check" (or a compromised session firing it in a loop) can no longer
// tie up the DB against unbounded rows. The RPC already caps to 50.
const INTEGRITY_MAX_TENANTS = 10000;
const INTEGRITY_MAX_LEASES  = 10000;
const INTEGRITY_MAX_LEDGER_PER_TENANT = 20000;
const INTEGRITY_MAX_RECURRING = 2000;
const INTEGRITY_MAX_ACCOUNTS = 5000;

export async function runDataIntegrityChecks(companyId, { deep = false } = {}) {
  const violations = [];
  try {
    // PM-9001: Unbalanced journal entries
    const { data: unbalancedJEs } = await supabase.rpc("find_unbalanced_jes", { p_company_id: companyId });
    if (unbalancedJEs?.length > 0) {
      for (const je of unbalancedJEs) {
        violations.push({ code: "PM-9001", details: `JE ${je.number} is out of balance by $${je.difference}`, meta: { jeId: je.id, jeNumber: je.number, difference: je.difference } });
      }
    }

    // PM-9002: Active tenants with no lease (deep only). Prefer tenant_id
    // matching (authoritative); fall back to case-insensitive name match
    // only for legacy lease rows without tenant_id. Previous eq("tenant_name")
    // was case-sensitive and reported false positives for any lease whose
    // tenant_name casing drifted from the tenant row.
    if (deep) {
      const { data: activeTenants } = await supabase.from("tenants").select("id, name, property").eq("company_id", companyId).is("archived_at", null).in("lease_status", ACTIVE_LEASE).limit(INTEGRITY_MAX_TENANTS);
      for (const t of (activeTenants || [])) {
        let q = supabase.from("leases").select("id").eq("company_id", companyId).eq("status", "active");
        // The name branch is paired with the property, otherwise a
        // NAMESAKE's active lease satisfies this check and the real
        // violation ("this tenant has no active lease") goes unreported.
        q = t.id
          ? q.or(`tenant_id.eq.${t.id},and(tenant_name.ilike.${pgrestQuote(escapeFilterValue(t.name))},property.eq.${pgrestQuote(t.property || "")})`)
          : q.ilike("tenant_name", escapeFilterValue(t.name)).eq("property", t.property || "");
        const { data: leaseRows } = await q.limit(1);
        if (!leaseRows || leaseRows.length === 0) {
          violations.push({ code: "PM-9002", details: `Tenant "${t.name}" at ${t.property} has no active lease`, meta: { tenantId: t.id, tenantName: t.name } });
        }
      }
    }

    // PM-9006: Tenant balance vs general-ledger mismatch (deep only).
    //
    // The cache (tenants.balance) is kept in sync with the GL by the
    // sync_tenant_balance_lines trigger (20260426000003), which sums
    // posted acct_journal_lines on the per-tenant AR account. The
    // sweep used to compare against ledger_entries (a separate
    // denormalized "tenant payment history" view); when the two
    // views diverged — which they do whenever a JE bypasses the
    // ledger_entries insert path — every drifted row produced a
    // false-positive PM-9006 even though the GL was correct. Compare
    // against the same source the trigger writes against so detection
    // matches truth.
    if (deep) {
      const { data: tenants } = await supabase.from("tenants").select("id, name, balance").eq("company_id", companyId).is("archived_at", null).limit(INTEGRITY_MAX_TENANTS);
      for (const t of (tenants || [])) {
        const { data: accts } = await supabase.from("acct_accounts").select("id").eq("company_id", companyId).eq("tenant_id", t.id);
        const accountIds = (accts || []).map(a => a.id);
        if (accountIds.length === 0) continue; // tenant has no per-AR account → nothing to check
        const { data: lines } = await supabase.from("acct_journal_lines")
          .select("debit, credit, journal_entry_id")
          .eq("company_id", companyId)
          .in("account_id", accountIds)
          .limit(INTEGRITY_MAX_LEDGER_PER_TENANT);
        if (!lines || lines.length === 0) {
          if (Math.abs(safeNum(t.balance)) > 0.01) {
            violations.push({ code: "PM-9006", details: `Tenant "${t.name}" balance ($${t.balance}) doesn't match GL ($0.00)`, meta: { tenantId: t.id, storedBalance: t.balance, glTotal: 0 } });
          }
          continue;
        }
        // Filter to posted JEs.
        const jeIds = [...new Set(lines.map(l => l.journal_entry_id))];
        // Chunked. .in() is a URL parameter, so a long list does not merely
        // truncate the RESULT -- it makes the request itself invalid, and an
        // integrity check that silently saw no entries would report every
        // line as unposted.
        const postedSet = new Set();
        for (let i = 0; i < jeIds.length; i += 100) {
          const { data: jes } = await supabase.from("acct_journal_entries")
            .select("id, status").eq("company_id", companyId)
            .in("id", jeIds.slice(i, i + 100));
          (jes || []).filter(j => j.status === "posted").forEach(j => postedSet.add(j.id));
        }
        let glTotal = 0;
        for (const l of lines) {
          if (!postedSet.has(l.journal_entry_id)) continue;
          glTotal += safeNum(l.debit) - safeNum(l.credit);
        }
        if (Math.abs(safeNum(t.balance) - glTotal) > 0.01) {
          violations.push({ code: "PM-9006", details: `Tenant "${t.name}" balance ($${t.balance}) doesn't match GL ($${glTotal.toFixed(2)})`, meta: { tenantId: t.id, storedBalance: t.balance, glTotal } });
        }
      }
    }

    // PM-9007: Recurring entries referencing inactive accounts
    // The schedule's own debit/credit accounts (template_lines_json does not
    // exist; the read failed and this check never ran -- FE-11 sweep).
    const { data: recurEntries } = await supabase.from("recurring_journal_entries").select("id, description, debit_account_id, credit_account_id").eq("company_id", companyId).eq("status", "active").limit(INTEGRITY_MAX_RECURRING);
    const { data: activeAccounts } = await supabase.from("acct_accounts").select("id").eq("company_id", companyId).eq("is_active", true).limit(INTEGRITY_MAX_ACCOUNTS);
    const activeAccountIds = new Set((activeAccounts || []).map(a => a.id));
    for (const re of (recurEntries || [])) {
      const UUIDISH = /^[0-9a-f-]{36}$/i;   // bare codes ("4000") resolve elsewhere
      const lines = [re.debit_account_id, re.credit_account_id].filter(a => a && UUIDISH.test(String(a))).map(account_id => ({ account_id }));
      for (const line of lines) {
        if (line.account_id && !activeAccountIds.has(line.account_id)) {
          violations.push({ code: "PM-9007", details: `Recurring entry "${re.description}" references inactive account`, meta: { recurId: re.id, accountId: line.account_id } });
          break;
        }
      }
    }

    // PM-9008: Active leases referencing archived properties (deep only)
    if (deep) {
      // leases.property (property_address never existed: the 400 was read as
      // "no leases" and this check found nothing, ever -- audit FE-11).
      const { data: activeLeases, error: lErr } = await supabase.from("leases").select("id, tenant_name, property, property_id").eq("company_id", companyId).eq("status", "active").limit(INTEGRITY_MAX_LEASES);
      const { data: liveProps, error: pErr } = await supabase.from("properties").select("id, address").eq("company_id", companyId).is("archived_at", null);
      if (lErr || pErr) pmError("PM-8006", { raw: lErr || pErr, context: "integrity check: active leases vs properties", silent: true });
      else {
        const liveIds = new Set((liveProps || []).map(p => String(p.id))), liveAddr = new Set((liveProps || []).map(p => p.address));
        for (const lease of (activeLeases || [])) {
          const live = lease.property_id ? liveIds.has(String(lease.property_id)) : liveAddr.has(lease.property);
          if (!live) violations.push({ code: "PM-9008", details: `Lease for "${lease.tenant_name}" references archived/missing property "${lease.property}"`, meta: { leaseId: lease.id } });
        }
      }
    }

    // Log all violations silently
    for (const v of violations) {
      pmError(v.code, { context: v.details, meta: v.meta, silent: true });
    }
  } catch (e) {
    pmError("PM-8006", { raw: e, context: "data integrity checks", silent: true });
  }
  return violations;
}

// ============ COMPANY SETTINGS ============
// Loads company settings, merging DB values over COMPANY_DEFAULTS.
// Returns { settings, loadError? }. Previously returned a bare object of
// defaults on fetch failure, which meant a transient DB error looked
// identical to "no row exists" — the Settings UI would render the
// defaults and, if the user saved, overwrite real stored values with
// them. Now surfaces the failure so callers can avoid the write path.
export async function loadCompanySettings(companyId) {
  if (!companyId) return { ...COMPANY_DEFAULTS, _loadError: null };
  try {
    const { data, error } = await supabase.from("company_settings").select("*").eq("company_id", companyId).maybeSingle();
    if (error) {
      pmError("PM-8006", { raw: error, context: "load company settings — fetch failed, returning defaults with _loadError flag", silent: true });
      return { ...COMPANY_DEFAULTS, _loadError: error.message || "Settings fetch failed" };
    }
    if (!data) return { ...COMPANY_DEFAULTS, _loadError: null };
    const merged = { ...COMPANY_DEFAULTS };
    for (const key of Object.keys(COMPANY_DEFAULTS)) {
      if (data[key] != null) merged[key] = data[key];
    }
    merged._loadError = null;
    return merged;
  } catch (e) {
    pmError("PM-8006", { raw: e, context: "load company settings", silent: true });
    return { ...COMPANY_DEFAULTS, _loadError: e.message || "Settings fetch crashed" };
  }
}

// Saves company settings (upsert).
export async function saveCompanySettings(companyId, settings, userEmail) {
  if (!companyId) throw new Error("companyId required");
  const row = { company_id: companyId, updated_at: new Date().toISOString(), updated_by: userEmail || "" };
  for (const key of Object.keys(COMPANY_DEFAULTS)) {
    if (settings[key] != null) row[key] = settings[key];
  }
  const { error } = await supabase.from("company_settings").upsert([row], { onConflict: "company_id" });
  if (error) throw new Error("Failed to save settings: " + error.message);
  return row;
}
